import { type ReactNode } from "react";
import { motion } from "motion/react";
import { Clock } from "lucide-react";
import { cn } from "@/modern/utils";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";
import { TEMPORAL_DESCRIPTIONS } from "../context/PromotePanelContext";

interface MarketingTemporalSelectorProps {
  temporalTags: MarketingTag[];
  selectedTagId: number | null;
  onSelect: (tagId: number) => void;
  /** Renders on the same row as the section title (e.g. step help). */
  headerEnd?: ReactNode;
  /** Shown below the section title, above the options. */
  subtitle?: ReactNode;
}

export const MarketingTemporalSelector = ({
  temporalTags,
  selectedTagId,
  onSelect,
  headerEnd,
  subtitle,
}: MarketingTemporalSelectorProps) => {
  if (temporalTags.length === 0 && headerEnd == null) return null;

  const headerBlock = (
    <>
      <div
        className={cn(
          "flex items-center justify-between gap-2 min-h-7",
          subtitle != null ? "mb-1" : "mb-1.5",
        )}
      >
        <label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground leading-none">
          Temporal Phase
        </label>
        {headerEnd != null ? <div className="flex shrink-0 items-center">{headerEnd}</div> : null}
      </div>
      {subtitle != null ? (
        <div className="text-sm text-muted-foreground/70 mb-4">{subtitle}</div>
      ) : null}
    </>
  );

  if (temporalTags.length === 0) {
    return <div>{headerBlock}</div>;
  }

  return (
    <div>
      {headerBlock}
      <div className="space-y-1.5">
        {temporalTags.map((tag) => {
          const isSelected = selectedTagId === tag.id;
          const description = TEMPORAL_DESCRIPTIONS[tag.label] ?? "";

          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onSelect(tag.id)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all duration-150",
                isSelected
                  ? "border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]"
                  : "border-border hover:border-muted-foreground/40 hover:bg-muted/30",
              )}
            >
              <div
                className={cn(
                  "w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-150",
                  isSelected ? "border-primary bg-primary/10" : "border-muted-foreground/25",
                )}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 600, damping: 25 }}
                    className="w-2 h-2 rounded-full bg-primary"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className={cn(
                  "text-sm font-semibold block transition-colors duration-150",
                  isSelected ? "text-primary" : "text-foreground",
                )}>
                  {tag.label}
                </span>
                {description && (
                  <span className="text-sm text-muted-foreground">{description}</span>
                )}
              </div>
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                >
                  <Clock className="w-3.5 h-3.5 text-primary" />
                </motion.div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
